import { useCallback, useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Hash, Loader2, Phone, Search } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import StatusBadge from '../../components/StatusBadge';
import Avatar from '../../components/Avatar';
import EmptyState from '../../components/EmptyState';
import Button from '../../components/Button';
import OrderDetailsDrawer from '../../components/OrderDetailsDrawer';
import AssignRiderDrawer from '../../components/AssignRiderDrawer';
import { useAuth } from '../../context/AuthContext';
import { useOrders } from '../../context/OrdersContext';
import {
  ORDER_QUEUE_ERRORS,
  assignRider as assignRiderApi,
  cancelOrder as cancelOrderApi,
  confirmOrder as confirmOrderApi,
  getOrderBill,
  listRiders,
  lookupOrders,
  packOrder as packOrderApi,
  resendBill as resendBillApi,
  unassignRider as unassignRiderApi,
} from '../../api/orders';
import { formatCurrency, timeAgo } from '../../data/mockData';
import styles from './OrderLookup.module.css';

const MODES = [
  { key: 'id', label: 'Order ID', icon: Hash, placeholder: 'Paste the full order ID' },
  { key: 'phone', label: 'Phone number', icon: Phone, placeholder: '10-digit phone number' },
];

function friendlyError(err) {
  return ORDER_QUEUE_ERRORS[err.message] || err.message || 'Something went wrong.';
}

function normalizePhoneQuery(value) {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length >= 10 ? digits.slice(-10) : String(value || '').trim();
}

export default function OrderLookup() {
  const { user } = useAuth();
  const { notify } = useOrders();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState('id');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [riders, setRiders] = useState([]);
  const [activeOrder, setActiveOrder] = useState(null);
  const [assignTargets, setAssignTargets] = useState([]);

  useEffect(() => {
    listRiders(user.idToken).then(setRiders).catch(() => {});
  }, [user.idToken]);

  const attachRider = useCallback((order) => ({
    ...order,
    rider: order.deliveryPersonSub
      ? (riders.find((r) => r.id === order.deliveryPersonSub) || { id: order.deliveryPersonSub, name: order.deliveryPersonSub, phone: null })
      : null,
  }), [riders]);

  const deepLinkOrderId = searchParams.get('orderId') || location.state?.orderId || '';
  const deepLinkPhone = searchParams.get('phone') || location.state?.phone || '';
  const seedOrders = location.state?.seedOrders;

  const runSearch = useCallback(async (e, overrides = {}) => {
    e?.preventDefault();
    const nextMode = overrides.mode || mode;
    const raw = (overrides.query ?? query).trim();
    if (!raw) return;
    const q = nextMode === 'phone' ? normalizePhoneQuery(raw) : raw;
    setLoading(true);
    setError('');
    setHasSearched(true);
    try {
      const params = nextMode === 'id' ? { orderId: q } : { phone: q };
      const data = await lookupOrders(params, user.idToken);
      setResults(data.orders.map(attachRider));
    } catch (err) {
      setResults([]);
      setError(err.message === 'not_found' ? '' : friendlyError(err));
    } finally {
      setLoading(false);
    }
  }, [mode, query, user.idToken, attachRider]);

  // Prefer orders already found in global search; otherwise look up from the URL/state.
  useEffect(() => {
    if (Array.isArray(seedOrders) && seedOrders.length > 0) {
      const nextMode = deepLinkPhone ? 'phone' : 'id';
      const nextQuery = deepLinkPhone
        ? normalizePhoneQuery(deepLinkPhone)
        : String(deepLinkOrderId || seedOrders[0]?.id || '').trim();
      setMode(nextMode);
      setQuery(nextQuery);
      setResults(seedOrders);
      setHasSearched(true);
      setLoading(false);
      setError('');
      return undefined;
    }

    if (!deepLinkOrderId && !deepLinkPhone) return undefined;

    const nextMode = deepLinkPhone ? 'phone' : 'id';
    const nextQuery = deepLinkPhone
      ? normalizePhoneQuery(deepLinkPhone)
      : String(deepLinkOrderId).trim();
    setMode(nextMode);
    setQuery(nextQuery);

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      setHasSearched(true);
      try {
        const params = nextMode === 'id' ? { orderId: nextQuery } : { phone: nextQuery };
        const data = await lookupOrders(params, user.idToken);
        if (cancelled) return;
        setResults(data.orders);
      } catch (err) {
        if (cancelled) return;
        setResults([]);
        setError(err.message === 'not_found' ? '' : friendlyError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [deepLinkOrderId, deepLinkPhone, seedOrders, user.idToken]);

  useEffect(() => {
    setResults((prev) => (prev.length ? prev.map(attachRider) : prev));
    setActiveOrder((prev) => (prev ? attachRider(prev) : prev));
  }, [attachRider]);

  const switchMode = (key) => {
    setMode(key);
    setQuery('');
    setResults([]);
    setHasSearched(false);
    setError('');
  };

  const refreshOrder = async (orderId) => {
    try {
      const data = await lookupOrders({ orderId }, user.idToken);
      const updated = data.orders[0] ? attachRider(data.orders[0]) : null;
      setResults((prev) => prev.map((o) => (o.id === orderId ? (updated || o) : o)));
      setActiveOrder((prev) => (prev && prev.id === orderId ? updated : prev));
    } catch {
      // leave the last-known state in place if the refresh itself fails
    }
  };

  const withAction = async (id, action, successMessage) => {
    try {
      await action();
      if (successMessage) notify(successMessage);
      await refreshOrder(id);
    } catch (err) {
      notify(friendlyError(err));
    }
  };

  const handleConfirm = (id) => withAction(id, () => confirmOrderApi(id, user.idToken), `Order ${id} confirmed`);
  const handleCancel = (id) => withAction(id, () => cancelOrderApi(id, undefined, user.idToken), `Order ${id} cancelled`);
  const handlePack = (id) => withAction(id, () => packOrderApi(id, user.idToken), `Order ${id} marked packed`);
  const handleUnassign = (id) => withAction(id, () => unassignRiderApi(id, user.idToken), `Rider unassigned from ${id}`);
  const handleResend = (id) => withAction(id, () => resendBillApi(id, user.idToken), `Invoice re-sent for ${id}`);

  const handleAssign = async (orderIds, riderId) => {
    const id = orderIds[0];
    const rider = riders.find((r) => r.id === riderId);
    await withAction(id, () => assignRiderApi(id, riderId, user.idToken), `${rider?.name || 'Rider'} assigned to ${id}`);
  };

  const handleViewBill = async (id) => {
    const tab = window.open('', '_blank');
    try {
      const bill = await getOrderBill(id, user.idToken);
      const url = bill.pdfPresignedUrl || bill.presignedUrl;
      if (!url) {
        tab?.close();
        notify('No bill has been generated for this order yet — use Resend invoice to generate one.');
        return;
      }
      if (tab) tab.location.href = url;
    } catch (err) {
      tab?.close();
      notify(friendlyError(err));
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Support"
        title="Order & Bill Lookup"
        subtitle="Find any order in seconds when a customer calls in."
      />

      <form className={styles.searchCard} onSubmit={runSearch}>
        <div className={styles.modeTabs}>
          {MODES.map(({ key, label, icon: Icon }) => (
            <button type="button" key={key} className={`${styles.modeTab} ${mode === key ? styles.modeTabActive : ''}`} onClick={() => switchMode(key)}>
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>
        <div className={styles.inputWrap}>
          {loading ? <Loader2 size={16} className={styles.spin} /> : <Search size={16} />}
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={MODES.find((m) => m.key === mode).placeholder}
            autoFocus
          />
          <Button type="submit" size="sm" variant="primary" disabled={!query.trim() || loading}>Search</Button>
        </div>
      </form>

      {!hasSearched ? (
        <EmptyState icon={Search} title="Search to get started" description="Look up an order by its ID or the customer's phone number." />
      ) : loading ? (
        <p className={styles.loadingText}>Searching…</p>
      ) : error ? (
        <EmptyState
          icon={AlertTriangle}
          title="Could not complete that search"
          description={error}
          action={<Button variant="outline" onClick={runSearch}>Retry</Button>}
        />
      ) : results.length === 0 ? (
        <EmptyState icon={Search} title="No matching orders" description="Double-check the order ID or phone number and try again." />
      ) : (
        <div className={styles.results}>
          {results.map((order) => (
            <button key={order.id} className={styles.resultRow} onClick={() => setActiveOrder(order)}>
              <Avatar name={order.customer.name || 'Customer'} size={38} />
              <div className={styles.resultInfo}>
                <p className={styles.resultId}>
                  {order.id}
                  {order.channel === 'SUBS' && (
                    <span style={{
                      marginLeft: 8,
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: '0.03em',
                      textTransform: 'uppercase',
                      color: '#B8850A',
                      background: '#FDF2D3',
                      padding: '2px 7px',
                      borderRadius: 999,
                    }}
                    >
                      Subscription
                    </span>
                  )}
                </p>
                <p className={styles.resultCustomer}>
                  {order.customer.name || 'Customer'}
                  {order.customer.phone ? ` · ${order.customer.phone}` : order.customer.sub ? ` · ID ${order.customer.sub.slice(0, 8)}…` : ''}
                </p>
              </div>
              <StatusBadge status={order.status} size="sm" />
              <div className={styles.resultRight}>
                <span className={styles.resultTotal}>{formatCurrency(order.total)}</span>
                <span className={styles.resultTime}>{timeAgo(order.placedAt)}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      <OrderDetailsDrawer
        order={activeOrder}
        onClose={() => setActiveOrder(null)}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        onPack={handlePack}
        onAssign={(order) => { setActiveOrder(null); setAssignTargets([order]); }}
        onUnassign={handleUnassign}
        onResend={handleResend}
        onViewBill={handleViewBill}
      />

      <AssignRiderDrawer
        orders={assignTargets}
        riders={riders}
        activeRideCounts={{}}
        onClose={() => setAssignTargets([])}
        onAssign={handleAssign}
      />
    </div>
  );
}
